import './css/app.css';
import 'vendor/lazysizes';
import 'components/page-transition';

import { App } from '@grafikr/components';
import header from 'layout/header';
import headerDrawerNavigation from 'layout/header-drawer-navigation';
import productLightBox from 'pages/product/images';

import tracking from 'framework/tracking';
import visualDiscounts from 'framework/visual-discounts';

import aosWrapper from 'components/aos-wrapper';
import cartCount from 'components/cart-count';

import cart from 'utils/cart';
import intersectionLoader from 'utils/loaders/intersection';
import onElementEvent from 'utils/loaders/on-element-event';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';
import onHashchange from 'utils/loaders/on-hashchange';
import onRegionAvailable from 'utils/loaders/on-region-available';

const app = new App({
  // Layout
  'geo-modal': [
    [onElementInteraction, onRegionAvailable],
    () => import(/* webpackChunkName: "layout-geo-modal" */ 'layout/geo-modal'),
  ],
  header,
  'search-modal': [
    onElementInteraction,
    () => import(/* webpackChunkName: "layout-search-modal" */ 'layout/search-modal'),
  ],
  'header-drawer-navigation': headerDrawerNavigation,

  // Components
  'character-select': [
    intersectionLoader,
    () =>
      import(/* webpackChunkName: "component-character-select" */ 'components/character-select'),
  ],
  'cart-count': cartCount,
  'address-form': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
  ],
  'add-class': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-add-class" */ 'components/add-class'),
  ],
  'video-scroll-content': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-video-scroll-content" */ 'components/video-scroll-content'
      ),
  ],
  'logo-slider': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-logo-slider" */ 'components/logo-slider'),
  ],
  'table-of-contents': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-table-of-contents" */ 'components/table-of-contents'),
  ],
  'blog-tags-slider': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-blog-tags-slider" */ 'components/blog-tags-slider'),
  ],
  'hero-collection': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-hero-collection" */ 'components/hero-collection'),
  ],
  'product-item': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-product-item" */ 'components/product-item'),
  ],
  'ver-collection-slider': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-video-scroll-content" */ 'components/ver-collection-slider'
      ),
  ],
  'ver-card-slider': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-card-content" */ 'components/ver-card-slider'),
  ],
  faq: [intersectionLoader, () => import(/* webpackChunkName: "component-faq" */ 'components/faq')],
  'expanding-scroll-content': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-expanding-scroll-content" */ 'components/expanding-scroll-content'
      ),
  ],
  carousel: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
  ],
  'carousel-2': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-carousel-2" */ 'components/carousel-2'),
  ],
  'expanding-carousel': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-carousel-2" */ 'components/expanding-carousel'),
  ],
  'text-marquee': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-text-marquee" */ 'components/text-marquee'),
  ],
  'text-masking': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-text-masking" */ 'components/text-masking'),
  ],
  'highlight-features-v2': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-highlight-features-v2" */ 'components/highlight-features-v2'
      ),
  ],
  'multiple-featured-col': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-multiple-featured-col" */ 'components/multiple-featured-col'
      ),
  ],
  'cart-drawer': [
    [onEvent, onElementInteraction, onHashchange],
    () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
  ],
  collapsible: [
    [intersectionLoader, onHashchange],
    () => import(/* webpackChunkName: "component-collapsible" */ 'components/collapsible'),
  ],
  'contact-form': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-contact-form" */ 'components/contact-form'),
  ],
  drawer: [
    onElementInteraction,
    () => import(/* webpackChunkName: "component-drawer" */ 'components/drawer'),
  ],
  'filter-drawer': [
    onElementEvent,
    () => import(/* webpackChunkName: "component-filter-drawer" */ 'components/filter-drawer'),
  ],
  'filter-sort': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-filter-sort" */ 'components/filter-sort'),
  ],
  'image-sequence': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-image-sequence" */ 'components/image-sequence'),
  ],
  'category-nav': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-category-nav" */ 'components/category-nav'),
  ],
  'lazy-content': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
  ],
  'newsletter-modal': [
    onElementInteraction,
    () =>
      import(/* webpackChunkName": "component-newsletter-modal" */ 'components/newsletter-modal'),
  ],
  'announcement-fade': [
    intersectionLoader,
    () =>
      import(/* webpackChunkName": "component-announcement-fade" */ 'components/announcement-fade'),
  ],
  'flowbox-dynamic-product': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName": "component-flowbox-dynamic-product" */ 'components/flowbox-dynamic-product'
      ),
  ],
  'klaviyo-form': [
    intersectionLoader,
    () => import(/* webpackChunkName": "component-klaviyo-form" */ 'components/klaviyo-form'),
  ],
  testimonials: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-testimonials" */ 'components/testimonials'),
  ],
  pagination: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
  ],
  'product-item-media': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-product-item-media" */ 'components/product-item-media'
      ),
  ],
  'product-media': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-product-media" */ 'components/product-media'),
  ],
  'product-image-feature': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-product-image-features" */ 'components/product-image-feature'
      ),
  ],
  'read-more': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-read-more" */ 'components/read-more'),
  ],
  'filter-sidebar': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-filter-sidebar" */ 'components/filter-sidebar'),
  ],
  'sort-option-values': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "component-sort-option-values" */ 'components/sort-option-values'
      ),
  ],
  'password-overlay': [
    intersectionLoader,
    () =>
      import(/* webpackChunkName: "component-password-overlay" */ 'components/password-overlay'),
  ],
  'password-video': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-password-video" */ 'components/password-video'),
  ],
  video: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-video" */ 'components/video'),
  ],
  vimeo: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-vimeo" */ 'components/vimeo'),
  ],
  'discount-prices': [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-discount-prices" */ 'components/discount-prices'),
  ],
  script: [
    intersectionLoader,
    () => import(/* webpackChunkName: "component-script" */ 'components/script'),
  ],

  // Collections
  'collection-promotion': [
    intersectionLoader,
    () => import(/* webpackChunkName: "collection-promotion" */ 'pages/collection/promotion'),
  ],

  // Pages
  product: [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
  ],
  'product-form': [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
  ],
  'product-recommendations': [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
  ],
  'product-store-inventory-drawer': [
    onElementInteraction,
    () =>
      import(
        /* webpackChunkName: "product-store-inventory-drawer" */ 'pages/product/store-inventory-drawer'
      ),
  ],
  'product-tabs': [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-tabs" */ 'pages/product/tabs'),
  ],
  'sticky-scroll': [
    intersectionLoader,
    () => import(/* webpackChunkName: "sticky-scroll" */ 'pages/product/sticky-scroll'),
  ],
  'product-swatches': [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-swatches" */ 'pages/product/swatches'),
  ],
  'option-text': [
    intersectionLoader,
    () => import(/* webpackChunkName: "option-text" */ 'pages/product/option-text'),
  ],

  guide: [intersectionLoader, () => import(/* webpackChunkName": "guide" */ 'pages/guide/index')],

  'product-lightbox': productLightBox,
  lightbox: [
    intersectionLoader,
    () => import(/* webpackChunkName: "lightbox" */ 'pages/product/lightbox'),
  ],
  'product-highlight-features': [
    intersectionLoader,
    () =>
      import(
        /* webpackChunkName: "product-highlight-features" */ 'pages/product/highlight-features'
      ),
  ],
  'product-bundles': [
    intersectionLoader,
    () => import(/* webpackChunkName: "product-bundles" */ 'pages/product/bundles'),
  ],
  'aos-wrapper': aosWrapper,
});

tracking(app);
visualDiscounts(app);

app.emitter.on('cart:added', async () => {
  const { data } = await cart.get();

  if (data) {
    theme.cart = data;
    app.emitter.emit('cart:updated', data);
  }
});

app.emitter.on('cart:added:no-drawer', async () => {
  const { data } = await cart.get();

  if (data) {
    theme.cart = data;
    app.emitter.emit('cart:updated:no-drawer', data);
  }
});

app.mount();

document.addEventListener('shopify:section:load', () => app.mount());
